import { WORKSHOP_GALLERY } from '../data/mock';
import { img, srcSet } from '../lib/img';

// Editorial gallery of real workshop sessions delivered at NIFT, NIT, coworking spaces, etc.
// Variant controls the surface (light surface vs dark).
export default function WorkshopGallery({ variant = 'light', limit }) {
  const items = limit ? WORKSHOP_GALLERY.slice(0, limit) : WORKSHOP_GALLERY;
  const dark = variant === 'dark';

  // Featured (first item) is wider; rest are uniform cards.
  const featured = items[0];
  const rest = items.slice(1);

  return (
    <section className={`py-20 lg:py-28 ${dark ? 'bg-charcoal-deep text-ivory' : 'bg-ivory-warm'}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 reveal">
          <div className="max-w-2xl">
            <p className={`text-[11px] tracking-[0.3em] uppercase mb-5 ${dark ? 'text-champagne' : 'text-champagne-deep'}`}>
              From The Lecture Halls
            </p>
            <h2 className={`font-serif text-4xl lg:text-5xl leading-[1.05] ${dark ? 'text-ivory' : 'text-charcoal'}`}>
              Real sessions. <em className="italic-accent">Real rooms.</em>
            </h2>
          </div>
          <p className={`max-w-md leading-relaxed ${dark ? 'text-ivory/65' : 'text-charcoal/70'}`}>
            Selected frames from workshops and guest sessions delivered at NIFT Bengaluru, NIT Trichy and professional communities across India.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 reveal" data-testid="workshop-gallery">
          {/* Featured tile */}
          {featured && (
            <figure
              data-testid={`workshop-${featured.id}`}
              className="group relative overflow-hidden lg:col-span-7 aspect-[4/3] lg:aspect-auto lg:min-h-[520px]"
            >
              <img
                loading="lazy"
                decoding="async"
                src={img(featured.src, 1600)}
                srcSet={srcSet(featured.src, [600, 1000, 1400, 1800])}
                sizes="(max-width: 1024px) 100vw, 55vw"
                alt={`${featured.title} — ${featured.venue}`}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1.4s]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal-deep/85 via-charcoal-deep/15 to-transparent" />
              <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                <p className="text-[10px] tracking-[0.3em] uppercase text-champagne mb-2">
                  {featured.venue}{featured.date ? ` · ${featured.date}` : ''} · {featured.kind}
                </p>
                <p className="font-serif text-xl md:text-2xl lg:text-3xl text-ivory leading-tight max-w-2xl">{featured.title}</p>
                {featured.caption && (
                  <p className="mt-3 text-sm text-ivory/75 max-w-2xl leading-relaxed hidden md:block">{featured.caption}</p>
                )}
              </figcaption>
            </figure>
          )}

          {/* Companion grid — adapts: 2 cols for ≤4 items, 3 cols for 5–6 items */}
          <div className={`lg:col-span-5 grid ${rest.length >= 5 ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2'} gap-4`}>
            {rest.map((w) => (
              <figure
                key={w.id}
                data-testid={`workshop-${w.id}`}
                className="group relative overflow-hidden aspect-square"
              >
                <img
                  loading="lazy"
                  decoding="async"
                  src={img(w.src, 900)}
                  srcSet={srcSet(w.src, [400, 600, 900])}
                  sizes="(max-width: 768px) 50vw, 18vw"
                  alt={`${w.title} — ${w.venue}`}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1.4s]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-deep/90 via-charcoal-deep/20 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-3 lg:p-4">
                  <p className="text-[9px] tracking-[0.3em] uppercase text-champagne mb-1">{w.venue}{w.date ? ` · ${w.date}` : ''}</p>
                  <p className="font-serif text-sm lg:text-base text-ivory leading-tight">{w.title}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
